import type { ReactNode } from "react"
import { memo, useEffect, useRef } from "react"
import { backgroundPreloader } from "@/services/background-preloader"

import { cn } from "@be-miniapp/ui/lib/utils"

import type { HomeBackground } from "@/hooks/use-optimized-home-data"

const TIER_BANNER_WIDTH = 540
const TIER_BANNER_HEIGHT = 313
const TIER_BANNER_IMAGE_CLASS =
  "pointer-events-none absolute inset-x-0 top-0 z-0 h-auto w-full max-w-full"

interface HomeBackgroundProps {
  background: HomeBackground
}

type HomeTierBackgroundProps = {
  homeContent?: string
  className?: string
  contentClassName?: string
  children?: ReactNode
}

/**
 * Memoized home background component
 * Handles background image display and preloading
 */
export const HomeBackgroundImage = memo<HomeBackgroundProps>(
  ({ background }) => {
    // Preload the image when background changes
    useEffect(() => {
      if (background.home_content) {
        backgroundPreloader.preloadBackgroundImage(background.home_content)
      }
    }, [background.home_content])

    if (!background.home_content) return null

    return (
      <img
        src={background.home_content}
        fetchPriority="high"
        className="absolute inset-x-0 top-0 h-auto w-full max-w-full"
        height={313}
        alt="Home background"
      />
    )
  }
)

HomeBackgroundImage.displayName = "HomeBackgroundImage"

export const HomeTierBackground = memo(
  ({
    homeContent,
    className,
    contentClassName,
    children,
  }: HomeTierBackgroundProps) => {
    const preloadedRef = useRef<string | null>(null)

    useEffect(() => {
      if (!homeContent || homeContent === preloadedRef.current) {
        return
      }

      preloadedRef.current = homeContent
      backgroundPreloader.preloadBackgroundImage(homeContent)
    }, [homeContent])

    return (
      <div className={cn("relative w-full overflow-hidden", className)}>
        {homeContent ? (
          <img
            alt=""
            className={TIER_BANNER_IMAGE_CLASS}
            fetchPriority="high"
            height={TIER_BANNER_HEIGHT}
            src={homeContent}
            width={TIER_BANNER_WIDTH}
          />
        ) : null}

        <div className={cn("relative z-10", contentClassName)}>{children}</div>
      </div>
    )
  }
)

HomeTierBackground.displayName = "HomeTierBackground"
